import type { ComponentProps } from 'react';
import React from 'react';

import { Dialog } from '@rtkwlf/fenrir-react';
import { useTranslation } from 'react-i18next';

export enum Appearances {
  Danger = 'danger',
  Default = 'default',
  Success = 'success',
  Warning = 'warning',
}

type DialogRootProps = ComponentProps<typeof Dialog.Root>;

interface ControlledDialogProps {
  isOpen: NonNullable<DialogRootProps['isOpen']>;
  onIsOpenChange: NonNullable<DialogRootProps['onIsOpenChange']>;
}

interface UncontrolledDialogProps {
  isOpen?: never;
  onIsOpenChange?: never;
}

interface CommonProps {
  trigger: React.ReactNode;
  title: string;
  description: React.ReactNode;
  cancelText?: string;
  actionText: string;
  actionIcon?: React.ReactNode;
  actionAppearance?: Appearances | undefined;
  actionIsLoading?: boolean;
  actionOnClick: () => void;
}

export type Props = CommonProps &
  (ControlledDialogProps | UncontrolledDialogProps);

export const ConfirmationDialog: React.FC<Props> = ({
  trigger,
  title,
  description,
  cancelText,
  actionText,
  actionIcon,
  actionAppearance,
  actionOnClick,
  actionIsLoading,
  isOpen,
  onIsOpenChange,
}) => {
  const { t } = useTranslation();

  const handleIsOpenChange = (isOpen: boolean) => {
    if (!onIsOpenChange) {
      return;
    }

    return () => onIsOpenChange(isOpen);
  };

  return (
    <Dialog.Root isOpen={isOpen}>
      <Dialog.Trigger onClick={handleIsOpenChange(true)} asChild>
        {trigger}
      </Dialog.Trigger>
      <Dialog.Content maxWidth="40rem">
        <Dialog.Header>
          <Dialog.HeaderTitle marginBottom="default">
            {title}
          </Dialog.HeaderTitle>
          <Dialog.HeaderDescription asChild={typeof description === 'object'}>
            {description}
          </Dialog.HeaderDescription>
        </Dialog.Header>
        <Dialog.Footer>
          <Dialog.FooterCancel onClick={handleIsOpenChange(false)}>
            {cancelText ?? t('ui:footer.cancelButtonText')}
          </Dialog.FooterCancel>
          <Dialog.FooterAction
            appearance={actionAppearance}
            onClick={actionOnClick}
            iconLeft={actionIcon}
            isLoading={actionIsLoading}
          >
            {actionText}
          </Dialog.FooterAction>
        </Dialog.Footer>
        <Dialog.Close onClick={handleIsOpenChange(false)} />
      </Dialog.Content>
    </Dialog.Root>
  );
};
